import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiClipboard,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";
import MenuLateral from "../../Components/MenuLateral";
import api from "../../services/api";
import { getApiErrorMessage } from "../../services/apiError";
import { showApiError } from "../../services/showApiError";
import Swal from "sweetalert2";
import "./index.css";

const COMMERCIAL_STATUSES = [
  { value: "PEDIDO_ORCADO", label: "Pedido orçado" },
  { value: "PEDIDO_REALIZADO", label: "Pedido realizado" },
  { value: "VENDA_CONCLUIDA", label: "Venda concluída" },
  { value: "PEDIDO_CANCELADO", label: "Pedido cancelado" },
];

const INITIAL_STATUSES = COMMERCIAL_STATUSES.filter((status) =>
  ["PEDIDO_ORCADO", "PEDIDO_REALIZADO"].includes(status.value),
);

const emptyItem = { productId: "", quantity: 1, eventIds: [] };

function getErrorMessage(error) {
  return getApiErrorMessage(error);
}

function getCommercialStatusLabel(status) {
  return COMMERCIAL_STATUSES.find((item) => item.value === status)?.label || status;
}

function getCommercialStatusTone(status) {
  if (status === "PEDIDO_REALIZADO") return "ready";
  if (status === "VENDA_CONCLUIDA") return "completed";
  if (status === "PEDIDO_CANCELADO") return "cancelled";
  return "quoted";
}

function CadVenda() {
  const navigate = useNavigate();
  const buyerInputRef = useRef(null);
  const [buyerPersonId, setBuyerPersonId] = useState("");
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerSearch, setBuyerSearch] = useState("");
  const [buyers, setBuyers] = useState([]);
  const [buyersOpen, setBuyersOpen] = useState(false);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyerSearchError, setBuyerSearchError] = useState("");
  const [activeBuyerIndex, setActiveBuyerIndex] = useState(-1);
  const [commercialStatus, setCommercialStatus] = useState("PEDIDO_ORCADO");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const loadBase = async () => {
    setLoading(true);
    try {
      const [productsResponse, eventsResponse] = await Promise.all([
        api.get("/products", { params: { limit: 100 } }),
        api.get("/events", { params: { limit: 100 } }),
      ]);
      setProducts(productsResponse.data.products || []);
      setEvents(eventsResponse.data.data || []);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get("/sales", {
        params: {
          search: search || undefined,
          commercialStatus: statusFilter || undefined,
          limit: 100,
        },
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBase();
    loadOrders();
    // A busca e o filtro de pedidos são ações explícitas para evitar consultas por caractere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const term = buyerSearch.trim();
    if (!term) {
      setBuyers([]);
      setBuyersLoading(false);
      setBuyerSearchError("");
      return undefined;
    }

    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setBuyersLoading(true);
      setBuyerSearchError("");
      try {
        const response = await api.get("/sales/buyers", {
          params: { search: term, limit: 20 },
        });
        if (mounted) setBuyers(response.data.buyers || []);
      } catch (error) {
        if (mounted) {
          setBuyers([]);
          setBuyerSearchError(getErrorMessage(error).replace(/<br\s*\/?\s*>/gi, " "));
          if (!error?.response?.data?.message) {
            showApiError(error, "Erro ao buscar compradores");
          }
        }
      } finally {
        if (mounted) setBuyersLoading(false);
      }
    }, 280);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [buyerSearch]);

  const resetBuyer = () => {
    setBuyerPersonId("");
    setSelectedBuyer(null);
    setBuyerSearch("");
    setBuyers([]);
    setBuyersOpen(true);
    setActiveBuyerIndex(-1);
    window.setTimeout(() => buyerInputRef.current?.focus(), 0);
  };

  const resetForm = () => {
    setBuyerPersonId("");
    setSelectedBuyer(null);
    setBuyerSearch("");
    setBuyers([]);
    setBuyersOpen(false);
    setActiveBuyerIndex(-1);
    setCommercialStatus("PEDIDO_ORCADO");
    setItems([{ ...emptyItem }]);
  };

  const selectBuyer = (buyer) => {
    setBuyerPersonId(buyer.id);
    setSelectedBuyer(buyer);
    setBuyerSearch("");
    setBuyersOpen(false);
    setActiveBuyerIndex(-1);
  };

  const handleBuyerSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setBuyersOpen(true);
      setActiveBuyerIndex((current) =>
        buyers.length ? (current + 1) % buyers.length : -1,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setBuyersOpen(true);
      setActiveBuyerIndex((current) =>
        buyers.length ? (current <= 0 ? buyers.length - 1 : current - 1) : -1,
      );
      return;
    }

    if (event.key === "Enter" && buyersOpen) {
      event.preventDefault();
      const buyer = buyers[activeBuyerIndex] || (buyers.length === 1 ? buyers[0] : null);
      if (buyer) selectBuyer(buyer);
      return;
    }

    if (event.key === "Escape") {
      setBuyersOpen(false);
      setActiveBuyerIndex(-1);
    }
  };

  const updateItem = (index, field, value) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const removeItem = (index) => {
    setItems((current) =>
      current.length === 1
        ? [{ ...emptyItem }]
        : current.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const saveOrder = async (event) => {
    event.preventDefault();

    if (!buyerPersonId) {
      Swal.fire("Comprador obrigatório", "Selecione a pessoa compradora do pedido.", "warning");
      return;
    }

    const validItems = items.filter((item) => item.productId);
    if (!validItems.length) {
      Swal.fire("Produto obrigatório", "Adicione ao menos um produto ao pedido.", "warning");
      return;
    }

    const invalidQuantity = validItems.some(
      (item) => !Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 1,
    );
    if (invalidQuantity) {
      Swal.fire("Quantidade inválida", "Cada item precisa de uma quantidade inteira maior que zero.", "warning");
      return;
    }

    if (commercialStatus === "PEDIDO_REALIZADO") {
      const itemWithoutEvent = validItems.some((item) => {
        const product = products.find((candidate) => candidate.id === item.productId);
        return product?.numberOfPhotos > 0 && item.eventIds.length === 0;
      });
      if (itemWithoutEvent) {
        Swal.fire(
          "Evento obrigatório",
          "Todo item que exige fotos precisa ter ao menos um evento vinculado antes de registrar o pedido realizado.",
          "warning",
        );
        return;
      }
    }

    const payload = {
      buyerPersonId,
      commercialStatus,
      items: validItems.map((item) => {
        const nextItem = {
          productId: item.productId,
          quantity: Number(item.quantity),
        };
        if (item.eventIds.length) nextItem.eventIds = item.eventIds;
        return nextItem;
      }),
    };

    setLoading(true);
    try {
      const response = await api.post("/sales", payload);
      await Swal.fire("Pedido registrado", "Pedido registrado com sucesso.", "success");
      resetForm();
      await loadOrders();
      if (response.data?.order?.id) {
        navigate(`/Vendas/${response.data.order.id}`);
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Erro", html: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const selectedItemCount = items.filter((item) => item.productId).length;

  return (
    <div className="pagina-vendas">
      <MenuLateral />

      <main className="vendas-conteudo">
        <header className="vendas-header">
          <div>
            <span className="vendas-eyebrow">Operação comercial</span>
            <h1>Pedidos comerciais</h1>
            <p>Registre o pedido com clareza para que a próxima etapa comece sem retrabalho.</p>
          </div>
          <div className="vendas-header-note" aria-label="Fluxo do pedido">
            <span className="vendas-header-note-icon"><FiClipboard aria-hidden="true" /></span>
            <span>
              <strong>Atendimento assistido</strong>
              <small>Pessoa, itens e eventos no mesmo lugar</small>
            </span>
          </div>
        </header>

        <form className="vendas-composer" onSubmit={saveOrder}>
          <div className="vendas-composer-main">
            <section className="vendas-panel vendas-buyer-panel" aria-labelledby="buyer-panel-title">
              <div className="vendas-panel-heading">
                <div className="vendas-panel-icon vendas-panel-icon-blue"><FiUser aria-hidden="true" /></div>
                <div>
                  <span className="vendas-panel-kicker">Pessoa compradora</span>
                  <h2 id="buyer-panel-title">Quem vai comprar?</h2>
                  <p>Busque pelo nome e selecione uma única pessoa para este pedido.</p>
                </div>
              </div>

              {!selectedBuyer ? (
                <div className="vendas-buyer-picker">
                  <span className="vendas-field-label">
                    Pessoa compradora <span>Obrigatório</span>
                  </span>
                  <div className={`vendas-search-box${buyersOpen ? " is-open" : ""}`}>
                    <FiSearch aria-hidden="true" />
                    <input
                      ref={buyerInputRef}
                      role="combobox"
                      aria-label="Pessoa compradora"
                      aria-autocomplete="list"
                      aria-controls="buyer-suggestions"
                      aria-expanded={buyersOpen}
                      aria-activedescendant={
                        activeBuyerIndex >= 0 ? `buyer-option-${buyers[activeBuyerIndex]?.id}` : undefined
                      }
                      placeholder="Digite parte do nome..."
                      value={buyerSearch}
                      onChange={(event) => {
                        setBuyerSearch(event.target.value);
                        setBuyersOpen(true);
                        setActiveBuyerIndex(-1);
                      }}
                      onFocus={() => setBuyersOpen(true)}
                      onBlur={() => window.setTimeout(() => setBuyersOpen(false), 160)}
                      onKeyDown={handleBuyerSearchKeyDown}
                      disabled={loading}
                      autoComplete="off"
                    />
                    {buyerSearch && (
                      <button
                        type="button"
                        className="vendas-search-clear"
                        aria-label="Limpar busca da pessoa compradora"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setBuyerSearch("");
                          setBuyers([]);
                          setActiveBuyerIndex(-1);
                          buyerInputRef.current?.focus();
                        }}
                        disabled={loading}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  {buyersOpen && (
                    <div id="buyer-suggestions" className="vendas-buyer-suggestions" role="listbox" aria-label="Pessoas encontradas">
                      {buyersLoading && <div className="vendas-suggestion-feedback" role="status">Buscando pessoas...</div>}
                      {!buyersLoading && buyerSearchError && (
                        <div className="vendas-suggestion-feedback is-error">{buyerSearchError}</div>
                      )}
                      {!buyersLoading && !buyerSearchError && !buyerSearch.trim() && (
                        <div className="vendas-suggestion-feedback">
                          Digite o nome para encontrar uma pessoa compradora.
                        </div>
                      )}
                      {!buyersLoading && !buyerSearchError && buyerSearch.trim() && buyers.length === 0 && (
                        <div className="vendas-suggestion-feedback">
                          Nenhuma pessoa encontrada com esse nome.
                        </div>
                      )}
                      {!buyersLoading && buyers.map((buyer, index) => (
                        <button
                          type="button"
                          role="option"
                          id={`buyer-option-${buyer.id}`}
                          aria-selected={index === activeBuyerIndex}
                          className={`vendas-buyer-option${index === activeBuyerIndex ? " is-active" : ""}`}
                          key={buyer.id}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => selectBuyer(buyer)}
                        >
                          <span className="vendas-buyer-avatar"><FiUser aria-hidden="true" /></span>
                          <span className="vendas-buyer-option-copy">
                            <strong>{buyer.name}</strong>
                            <small>Selecionar pessoa compradora</small>
                          </span>
                          <FiArrowRight aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  )}
                  <small className="vendas-field-help">A busca considera qualquer trecho do nome.</small>
                </div>
              ) : (
                <div className="vendas-selected-buyer" aria-label={`Pessoa compradora selecionada: ${selectedBuyer.name}`}>
                  <span className="vendas-buyer-avatar"><FiCheck aria-hidden="true" /></span>
                  <span className="vendas-selected-buyer-copy">
                    <small>Selecionada para este pedido</small>
                    <strong>{selectedBuyer.name}</strong>
                  </span>
                  <button
                    type="button"
                    className="vendas-change-buyer"
                    onClick={resetBuyer}
                    disabled={loading}
                  >
                    Trocar pessoa
                  </button>
                </div>
              )}
            </section>

            <section className="vendas-panel" aria-labelledby="items-panel-title">
              <div className="vendas-section-heading">
                <div>
                  <span className="vendas-panel-kicker">Composição</span>
                  <h2 id="items-panel-title">Itens do pedido</h2>
                </div>
                <span className="vendas-section-count">{items.length} {items.length === 1 ? "item" : "itens"}</span>
              </div>

              <div className="vendas-items-list">
                {items.map((item, index) => (
                  <fieldset className="vendas-item-card" key={`order-item-${index}`}>
                    <legend>Item {index + 1}</legend>
                    <label>
                      Produto
                      <select
                        aria-label={`Produto do item ${index + 1}`}
                        value={item.productId}
                        onChange={(event) => updateItem(index, "productId", event.target.value)}
                        disabled={loading}
                      >
                        <option value="">Selecione o produto</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Quantidade
                      <input
                        aria-label={`Quantidade do item ${index + 1}`}
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={(event) => updateItem(index, "quantity", event.target.value)}
                        disabled={loading}
                      />
                    </label>
                    <label>
                      Eventos vinculados
                      <select
                        aria-label={`Eventos do item ${index + 1}`}
                        multiple
                        size={Math.min(Math.max(events.length, 2), 4)}
                        value={item.eventIds}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "eventIds",
                            Array.from(event.target.selectedOptions, (option) => option.value),
                          )
                        }
                        disabled={loading}
                      >
                        {events.map((listedEvent) => (
                          <option key={listedEvent.id} value={listedEvent.id}>
                            {listedEvent.name}
                          </option>
                        ))}
                      </select>
                      <small>Selecione um ou mais eventos quando o item exigir fotos.</small>
                    </label>
                    <button
                      type="button"
                      className="vendas-remove-item"
                      onClick={() => removeItem(index)}
                      disabled={loading}
                    >
                      <FiTrash2 aria-hidden="true" />
                      Remover item
                    </button>
                  </fieldset>
                ))}
              </div>

              <button
                type="button"
                className="vendas-button vendas-button-secondary vendas-add-item"
                onClick={() => setItems((current) => [...current, { ...emptyItem }])}
                disabled={loading}
              >
                <FiPlus aria-hidden="true" />
                Adicionar item
              </button>
            </section>

            <section className="vendas-panel vendas-status-panel" aria-labelledby="status-panel-title">
              <div className="vendas-status-symbol"><FiClipboard aria-hidden="true" /></div>
              <div className="vendas-status-copy">
                <span className="vendas-panel-kicker">Próxima etapa</span>
                <h2 id="status-panel-title">Como o pedido começa?</h2>
                <p>Escolha o estado que melhor representa o atendimento neste momento.</p>
              </div>
              <label className="vendas-status-select">
                Estado inicial
                <select
                  aria-label="Estado inicial"
                  value={commercialStatus}
                  onChange={(event) => setCommercialStatus(event.target.value)}
                  disabled={loading}
                >
                  {INITIAL_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
            </section>
          </div>

          <aside className="vendas-summary" aria-label="Resumo do pedido">
            <div className="vendas-summary-heading">
              <span className="vendas-summary-eyebrow">Rascunho atual</span>
              <FiClipboard aria-hidden="true" />
            </div>
            <h2>Pedido em construção</h2>
            <p className="vendas-summary-intro">Confira os dados essenciais antes de registrar.</p>

            <div className={`vendas-summary-buyer${selectedBuyer ? " is-selected" : ""}`}>
              <span className="vendas-summary-label">Pessoa compradora</span>
              <strong>{selectedBuyer?.name || "Ainda não selecionada"}</strong>
              {!selectedBuyer && <small>Obrigatória para continuar</small>}
            </div>

            <dl className="vendas-summary-list">
              <div>
                <dt>Itens preenchidos</dt>
                <dd>{selectedItemCount}</dd>
              </div>
              <div>
                <dt>Estado inicial</dt>
                <dd>{getCommercialStatusLabel(commercialStatus)}</dd>
              </div>
            </dl>

            <div className="vendas-summary-note">
              <FiCheck aria-hidden="true" />
              <span>O pedido poderá ser detalhado e acompanhado depois do registro.</span>
            </div>

            <div className="vendas-summary-actions">
              <button type="submit" className="vendas-button vendas-button-primary" disabled={loading}>
                <FiArrowRight aria-hidden="true" />
                Registrar pedido
              </button>
              <button type="button" className="vendas-button vendas-button-ghost" onClick={resetForm} disabled={loading}>
                Limpar rascunho
              </button>
            </div>
          </aside>
        </form>

        <section className="vendas-orders" aria-label="Lista de pedidos">
          <div className="vendas-orders-heading">
            <div>
              <span className="vendas-panel-kicker">Acompanhamento</span>
              <h2>Pedidos recentes</h2>
            </div>
            <span className="vendas-orders-count">{orders.length} {orders.length === 1 ? "resultado" : "resultados"}</span>
          </div>

          <div className="vendas-toolbar">
            <label>
              Buscar pedidos por pessoa compradora
              <input
                aria-label="Buscar pedidos por pessoa compradora"
                placeholder="Nome da pessoa..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                disabled={loading}
              />
            </label>
            <label>
              Estado comercial
              <select
                aria-label="Filtrar estado comercial"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                disabled={loading}
              >
                <option value="">Todos os estados</option>
                {COMMERCIAL_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="vendas-button vendas-button-secondary vendas-toolbar-button" onClick={loadOrders} disabled={loading}>
              <FiSearch aria-hidden="true" />
              Buscar pedidos
            </button>
          </div>

          {loading && <p className="vendas-loading" role="status">Atualizando pedidos...</p>}

          <div className="vendas-table-wrap">
            <table className="vendas-table">
              <thead>
                <tr>
                  <th>Pessoa compradora</th>
                  <th>Estado comercial</th>
                  <th>Itens</th>
                  <th>Registrado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td data-label="Pessoa compradora">{order.buyer?.name || "Pessoa indisponível"}</td>
                    <td data-label="Estado comercial">
                      <span className={`vendas-status-badge ${getCommercialStatusTone(order.commercialStatus)}`}>
                        {getCommercialStatusLabel(order.commercialStatus)}
                      </span>
                    </td>
                    <td data-label="Itens">{order.items?.length || 0}</td>
                    <td data-label="Registrado em">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "-"}
                    </td>
                    <td data-label="Ações">
                      <button
                        type="button"
                        className="vendas-table-action"
                        onClick={() => navigate(`/Vendas/${order.id}`)}
                        disabled={loading}
                      >
                        Detalhar pedido <FiArrowRight aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && !loading && (
                  <tr>
                    <td className="vendas-empty-row" colSpan="5">
                      <FiClipboard aria-hidden="true" />
                      <strong>Nenhum pedido encontrado</strong>
                      <span>Ajuste os filtros ou registre um novo pedido acima.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CadVenda;
